/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest(".js-product");
      this.productForm = this.section.querySelector(".js-product-form");
      this.optionSelectors = this.querySelectorAll(
        ".option-selector:not(.option-selector--custom)"
      );
      this.nativeSelector = document.getElementById(
        `variants-${this.section.dataset.section}`
      );
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.injectSaleBadgeStyles();
      this.updatePriceDifference();
      this.updateAvailability();
      this.updateSaleBadges();
      this.updateAddToCartButton();

      this.addEventListener("change", this.handleVariantChange.bind(this));

      this.setAttribute("loaded", "");
    }

    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection =
        !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      this.updatePriceDifference();
      this.updateSaleBadges();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(
        new CustomEvent("on:variant:change", {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            allVariants: this.data.variants,
            selectedOptions: this.selectedOptions,
          },
        })
      );
    }

    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn =
        this.addBtn || this.productForm.querySelector('[name="add"]');

      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant
        ? theme.strings.noStock
        : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists
        ? theme.strings.noStock
        : theme.strings.noVariant;

      el.classList.toggle("is-unavailable", !available);
      el.classList.toggle("is-nonexistent", !exists);

      if (optionEl.classList.contains("custom-select__option")) {
        const em = el.querySelector("em");

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute("title");
      }
    }

    updateAvailability() {
      if (this.dataset.showAvailability === "false") return;

      const { availabilityMode } = this.dataset;
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMode === "selection") {
        this.querySelectorAll(".js-option").forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (
                option === currVariant.options[optionIndex] &&
                optionIndex !== selectorIndex
              ) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll(".js-option");

              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === "dropdown") {
                  return opt.dataset.value === variant.options[selectorIndex];
                }

                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(
                  optionEl,
                  true,
                  variant.available
                );
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll(
            '.js-option:not([data-value=""])'
          );

          options.forEach((option) => {
            const optionValue =
              selector.dataset.selectorType === "dropdown"
                ? option.dataset.value
                : option.value;

            let variantsExist = false;
            let variantsAvailable = false;

            this.data.variants.forEach((v) => {
              let matches = 0;

              for (let i = 0; i < selectorIndex; i += 1) {
                if (
                  v.options[i] === this.selectedOptions[i] ||
                  this.selectedOptions[i] === null
                ) {
                  matches += 1;
                }
              }

              if (
                v.options[selectorIndex] === optionValue &&
                matches === selectorIndex
              ) {
                variantsExist = true;

                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });

            VariantPicker.updateOptionAvailability(
              option,
              variantsExist,
              variantsAvailable
            );
          });
        });
      }
    }

    injectSaleBadgeStyles() {
      if (document.getElementById("variant-picker-sale-badge-styles")) return;

      const style = document.createElement("style");
      style.id = "variant-picker-sale-badge-styles";
      style.textContent = `
        .opt-label--has-sale,
        .opt-label--has-sale-active,
        .opt-label--has-sale-muted {
          position: relative;
          overflow: visible;
        }

        .swatch-sale-badge {
          position: absolute;
          right: -8px;
          bottom: -6px;
          z-index: 3;
          min-width: 28px;
          height: 20px;
          padding: 0 7px;
          border-radius: 999px;
          background: var(--product-label-overlay-reduction-bg);
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          line-height: 20px;
          text-align: center;
          pointer-events: none;
          white-space: nowrap;
          box-sizing: border-box;
          opacity: 1;
          transition: opacity 0.2s ease;
        }

        .swatch-sale-badge span {
          display: block;
        }

        .opt-label--has-sale-muted .swatch-sale-badge {
          opacity: 0.42;
        }

        .opt-label--has-sale-active .swatch-sale-badge {
          opacity: 1;
        }

        .opt-label--swatch .swatch-sale-badge,
        .opt-label--image .swatch-sale-badge {
          right: -7px;
          bottom: -5px;
        }

        .opt-label--btn .swatch-sale-badge {
          right: -8px;
          top: -9px;
          bottom: auto;
        }

        @media (max-width: 749px) {
          .swatch-sale-badge {
            right: -6px;
            bottom: -5px;
            min-width: 24px;
            height: 18px;
            padding: 0 6px;
            font-size: 10px;
            line-height: 18px;
          }

          .opt-label--btn .swatch-sale-badge {
            right: -7px;
            top: -8px;
            bottom: auto;
          }
        }
      `;

      document.head.appendChild(style);
    }

    getSaleText(variant, mode = "percent") {
      if (!variant || !variant.compare_at_price || !variant.price) return null;
    
      const price = Number(variant.price);
      const compare = Number(variant.compare_at_price);
    
      if (!compare || compare <= price) return null;
    
      if (mode === "label") {
        return "Promo";
      }
    
      const percent = Math.max(1, Math.round((1 - price / compare) * 100));
    
      return `-${percent}%`;
    }

    getSaleScore(variant) {
      if (!variant || !variant.compare_at_price || !variant.price) return 0;

      const price = Number(variant.price);
      const compare = Number(variant.compare_at_price);

      if (!compare || compare <= price) return 0;

      return compare - price;
    }

    getBestSaleVariantForTestedOption(selectorIndex, optionValue) {
      let bestSaleVariant = null;
      let bestSaleScore = -1;

      this.data.variants.forEach((variant) => {
        if (!this.getSaleText(variant)) return;

        if (variant.options[selectorIndex] !== optionValue) return;

        const isCompatibleWithPreviousOptions = variant.options.every(
          (variantOptionValue, variantOptionIndex) => {
            if (variantOptionIndex === selectorIndex) {
              return true;
            }

            if (variantOptionIndex > selectorIndex) {
              return true;
            }

            const selectedOptionValue = this.selectedOptions[variantOptionIndex];

            if (!selectedOptionValue) return true;

            return variantOptionValue === selectedOptionValue;
          }
        );

        if (!isCompatibleWithPreviousOptions) return;

        const score = this.getSaleScore(variant);

        if (score > bestSaleScore) {
          bestSaleScore = score;
          bestSaleVariant = variant;
        }
      });

      return bestSaleVariant;
    }

    clearSaleBadge(label) {
      if (!label) return;

      label.classList.remove(
        "opt-label--has-sale",
        "opt-label--has-sale-active",
        "opt-label--has-sale-muted"
      );

      const existingBadge = label.querySelector(".swatch-sale-badge");
      if (existingBadge) existingBadge.remove();
    }

    addSaleBadge(label, saleText, isActive = false) {
      if (!label || !saleText) return;

      this.clearSaleBadge(label);

      label.classList.add("opt-label--has-sale");

      if (isActive) {
        label.classList.add("opt-label--has-sale-active");
      } else {
        label.classList.add("opt-label--has-sale-muted");
      }

      const badge = document.createElement("span");
      badge.className = "swatch-sale-badge";
      badge.innerHTML = `<span>${saleText}</span>`;

      label.appendChild(badge);
    }

updateSaleBadges() {
  const lastSelectorIndex = this.optionSelectors.length - 1;

  this.optionSelectors.forEach((selector, selectorIndex) => {
    const options = selector.querySelectorAll(".js-option");

    options.forEach((optionEl) => {
      const optionValue =
        selector.dataset.selectorType === "dropdown"
          ? optionEl.dataset.value
          : optionEl.value;

      const label =
        selector.dataset.selectorType === "dropdown"
          ? optionEl
          : optionEl.nextElementSibling;

      this.clearSaleBadge(label);

      if (!optionValue) return;

      const testedVariant = this.getBestSaleVariantForTestedOption(
        selectorIndex,
        optionValue
      );

      if (!testedVariant) return;

      const saleText =
        selectorIndex === lastSelectorIndex
          ? this.getSaleText(testedVariant, "percent")
          : this.getSaleText(testedVariant, "label");

      if (!saleText) return;

      const isActive =
        selector.dataset.selectorType === "dropdown"
          ? optionEl.getAttribute("aria-selected") === "true"
          : optionEl.checked;

      this.addSaleBadge(label, saleText, isActive);
    });
  });
}

    updateBackorderText() {
      this.backorder =
        this.backorder || this.section.querySelector(".backorder");

      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === "none") {
          const backorderProdEl = this.backorder.querySelector(
            ".backorder__product"
          );
          const prodTitleEl = this.section.querySelector(".product-title");
          const variantTitle = this.variant.title.includes("Default")
            ? ""
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    static updateLabelText(evt) {
      const selector = evt.target.closest(".option-selector");
      if (selector.dataset.selectorType === "dropdown") return;

      const colorText = selector.querySelector(".js-color-text");
      if (!colorText) return;

      colorText.textContent =
        evt.target.nextElementSibling.querySelector(".js-value").textContent;
    }

    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability ||
        this.section.querySelector("pickup-availability");

      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute("available");
        this.pickUpAvailability.innerHTML = "";
      }
    }

    updatePrice() {
      this.price =
        this.price || this.section.querySelector(".product-price .price");

      if (!this.price) return;

      let { variant } = this;

      if (this.preSelection) {
        variant = this.data.variants[0];

        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) {
            variant = this.data.variants[i];
          }
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector(".price__current");
        const priceWasEl = this.price.querySelector(".price__was");
        const unitPriceEl = this.price.querySelector(".unit-price");

        priceCurrentEl.innerHTML = this.data.formatted[variant.id].price;

        if (priceWasEl) {
          priceWasEl.innerHTML =
            this.data.formatted[variant.id].compareAtPrice || "";
        }

        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector(".unit-price__price");
          const unitEl = this.price.querySelector(".unit-price__unit");
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle(
          "price--on-sale",
          variant.compare_at_price > variant.price
        );

        this.price.classList.toggle(
          "price--sold-out",
          !variant.available && !this.preSelection
        );
      }

      this.price.querySelector(".price__default").hidden =
        !this.variant && !this.preSelection;

      this.price.querySelector(".price__no-variant").hidden =
        this.variant || this.preSelection;

      const from = this.price.querySelector(".price__from");

      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    updateSku() {
      this.sku = this.sku || this.section.querySelector(".product-sku__value");

      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;

      this.sku.textContent = skuAvailable ? this.variant.sku : "";
      this.sku.parentNode.hidden = !skuAvailable;
    }

    updateBarcode() {
      this.barcode =
        this.barcode || this.section.querySelector(".product-barcode__value");

      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;

      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : "";
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    updateUrl(evt) {
      if (!evt || evt.type !== "change" || this.dataset.updateUrl === "false")
        return;

      const url = this.variant
        ? `${this.dataset.url}?variant=${this.variant.id}`
        : this.dataset.url;

      window.history.replaceState({}, "", url);
    }

    updateVariantInput() {
      this.forms =
        this.forms ||
        this.section.querySelectorAll(".js-product-form, .js-instalments-form");

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : "";
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    updatePriceDifference() {
      this.querySelectorAll(".price-difference").forEach((el) => el.remove());
    }

    formatMoney(cents) {
      const currency = "€";
      const price = (cents / 100).toFixed(2);

      return `${price.replace(".", ",")}${currency}`;
    }

    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');

      return JSON.parse(dataEl.textContent);
    }

    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === "dropdown") {
          const selected = selector.querySelector(
            '.custom-select__option[aria-selected="true"]'
          );

          selectedOptions.push(
            selected && selected.dataset.value !== ""
              ? selected.dataset.value
              : null
          );
        } else {
          const selected = selector.querySelector("input:checked");
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    getSelectedVariant() {
      return this.data.variants.find((v) =>
        v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }
  }

  customElements.define("variant-picker", VariantPicker);
}